/*
 * RapidClean — boekingspagina (<rc-booking>), zie sections/booking.liquid.
 * Bouwt dagen en tijdsloten op uit de openingstijden, houdt de samenvatting bij
 * en zet bij afronden de afspraak als regel-eigenschappen in de winkelwagen,
 * waarna de klant direct naar de checkout gaat.
 */
if (!customElements.get('rc-booking')) {
  customElements.define(
    'rc-booking',
    class RcBooking extends HTMLElement {
      connectedCallback() {
        const configEl = this.querySelector('[data-booking-config]');
        if (!configEl) return;
        this.config = JSON.parse(configEl.textContent);
        this.periods = this.config.schedule.map((text) => this.parsePeriods(text));
        this.blocked = this.parseBlocked(this.config.blocked || []);
        this.config.selfLocations = this.config.selfLocations.map((l) => l.trim()).filter(Boolean);

        this.state = { service: null, date: null, time: null, dayOffset: 0 };
        this.dateFmt = new Intl.DateTimeFormat('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' });

        this.form = this.querySelector('[data-booking-form]');
        this.daysEl = this.querySelector('[data-days]');
        this.timesEl = this.querySelector('[data-times]');

        this.days = this.buildDays();
        this.renderSelfLocations();
        this.bindEvents();
        this.preselectService();
        this.renderDays();
        this.update();
      }

      /* ---------- Data ---------- */

      buildDays() {
        const days = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        for (let i = 0; i <= this.config.maxDaysAhead; i++) {
          const date = new Date(today);
          date.setDate(today.getDate() + i);
          if (this.periods[date.getDay()].length) days.push(date);
        }
        return days;
      }

      slotsFor(date, service) {
        if (!service) return [];
        const slots = [];
        const earliest = Date.now() + this.config.minHoursAhead * 3600 * 1000;
        const blocked = this.blocked[this.key(date)] || [];
        for (const [start, end] of this.periods[date.getDay()]) {
          for (let minutes = start; minutes + service.duration <= end; minutes += service.duration) {
            const slot = new Date(date);
            slot.setHours(0, minutes, 0, 0);
            if (slot.getTime() < earliest) continue;
            // Eén auto tegelijk: elk overlappend geblokkeerd moment sluit het slot uit
            const overlaps = blocked.some(([from, to]) => minutes < to && from < minutes + service.duration);
            if (!overlaps) slots.push(minutes);
          }
        }
        return slots;
      }

      get service() {
        return this.config.services.find((s) => s.id === this.state.service) || null;
      }

      get needsPickupChoice() {
        return this.service && this.service.location === 'rapidclean';
      }

      get pickupMode() {
        const checked = this.form.querySelector('input[name="pickup"]:checked');
        return checked ? checked.value : 'pickup';
      }

      get needsAddress() {
        if (!this.service) return false;
        return this.service.location === 'customer' || this.pickupMode === 'pickup';
      }

      get payment() {
        const checked = this.form.querySelector('input[name="payment"]:checked');
        return checked ? checked.value : 'full';
      }

      /** '09:00-12:00, 14:00-23:00' → [[540, 720], [840, 1380]] */
      parsePeriods(text) {
        return String(text || '')
          .split(',')
          .map((part) => part.match(/(\d{1,2})[:.](\d{2})\s*-\s*(\d{1,2})[:.](\d{2})/))
          .filter(Boolean)
          .map((m) => [Number(m[1]) * 60 + Number(m[2]), Number(m[3]) * 60 + Number(m[4])])
          .filter(([from, to]) => to > from);
      }

      /** Regels als '04-11-2026 10:00-11:00' of '2026-11-04' → { '2026-11-04': [[600, 660]] } */
      parseBlocked(lines) {
        const result = {};
        lines.forEach((line) => {
          const text = String(line).trim();
          let match = text.match(/^(\d{1,2})-(\d{1,2})-(\d{4})/);
          let key;
          if (match) {
            key = `${match[3]}-${match[2].padStart(2, '0')}-${match[1].padStart(2, '0')}`;
          } else {
            match = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
            if (!match) return;
            key = `${match[1]}-${match[2].padStart(2, '0')}-${match[3].padStart(2, '0')}`;
          }
          const times = this.parsePeriods(text.slice(match[0].length));
          (result[key] = result[key] || []).push(...(times.length ? times : [[0, 24 * 60]]));
        });
        return result;
      }

      /* ---------- Rendering ---------- */

      renderSelfLocations() {
        const wrap = this.querySelector('[data-self-locations]');
        wrap.innerHTML = this.config.selfLocations
          .map(
            (loc, i) =>
              `<label class="bk-option bk-option--chip"><input type="radio" name="self_location" value="${this.escape(loc)}" class="bk-radio"${i === 0 ? ' checked' : ''}><span><strong>${this.escape(loc)}</strong></span></label>`
          )
          .join('');
      }

      renderDays() {
        const visible = this.visibleDayCount();
        const maxOffset = Math.max(0, this.days.length - visible);
        this.state.dayOffset = Math.min(Math.max(0, this.state.dayOffset), maxOffset);
        const slice = this.days.slice(this.state.dayOffset, this.state.dayOffset + visible);
        const todayKey = this.key(new Date());
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowKey = this.key(tomorrow);

        this.daysEl.innerHTML = slice
          .map((date) => {
            const key = this.key(date);
            const available = !this.service || this.slotsFor(date, this.service).length > 0;
            const label = key === todayKey ? 'Vandaag' : key === tomorrowKey ? 'Morgen' : date.toLocaleDateString('nl-NL', { weekday: 'short' });
            const selected = this.state.date === key;
            return `<button type="button" class="bk-day${selected ? ' is-selected' : ''}" data-day="${key}" role="radio" aria-checked="${selected}"${available ? '' : ' disabled'}>
              <span class="bk-day-name">${label}</span>
              <span class="bk-day-num">${date.getDate()}</span>
              <span class="bk-day-month">${date.toLocaleDateString('nl-NL', { month: 'short' })}</span>
            </button>`;
          })
          .join('');

        const first = slice[0];
        const last = slice[slice.length - 1];
        const monthEl = this.querySelector('[data-month]');
        if (first) {
          const a = first.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });
          const b = last.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });
          monthEl.textContent = a === b ? a : `${first.toLocaleDateString('nl-NL', { month: 'long' })} – ${b}`;
        }
        this.querySelector('[data-days-prev]').disabled = this.state.dayOffset === 0;
        this.querySelector('[data-days-next]').disabled = this.state.dayOffset >= maxOffset;
      }

      renderTimes() {
        const hint = this.querySelector('[data-times-hint]');
        const service = this.service;
        if (!service) {
          this.timesEl.innerHTML = '';
          hint.textContent = 'Kies eerst een behandeling om de beschikbare tijden te zien.';
          hint.hidden = false;
          return;
        }
        if (!this.state.date) {
          this.timesEl.innerHTML = '';
          hint.textContent = 'Kies een dag.';
          hint.hidden = false;
          return;
        }
        const slots = this.slotsFor(this.parseKey(this.state.date), service);
        if (!slots.includes(this.state.time)) this.state.time = null;
        hint.textContent = `Tijdsblok van ${service.duration} minuten.`;
        hint.hidden = false;
        this.timesEl.innerHTML = slots
          .map((minutes) => {
            const selected = this.state.time === minutes;
            return `<button type="button" class="bk-time${selected ? ' is-selected' : ''}" data-time="${minutes}" role="radio" aria-checked="${selected}">${this.fmtTime(minutes)}</button>`;
          })
          .join('');
      }

      update() {
        const service = this.service;
        this.querySelector('[data-pickup-group]').hidden = !this.needsPickupChoice;
        this.querySelector('[data-self-group]').hidden = !(this.needsPickupChoice && this.pickupMode === 'self');
        this.querySelector('[data-address-group]').hidden = !this.needsAddress;
        this.querySelector('[data-address-label]').textContent =
          service && service.location === 'customer' ? 'Waar mogen we je auto reinigen?' : 'Waar mogen we je auto ophalen?';

        // Datum ongeldig voor deze behandeling? Kies de eerstvolgende dag met tijden.
        if (service) {
          const valid = this.state.date && this.slotsFor(this.parseKey(this.state.date), service).length > 0;
          if (!valid) {
            const first = this.days.find((d) => this.slotsFor(d, service).length > 0);
            this.state.date = first ? this.key(first) : null;
            if (first) this.state.dayOffset = Math.max(0, this.days.indexOf(first) - 0);
          }
        }
        this.renderDays();
        this.renderTimes();

        // Betaalopties
        const deposit = this.config.deposit;
        const fullLabel = this.querySelector('[data-pay-full]');
        const depositLabel = this.querySelector('[data-pay-deposit]');
        if (service) {
          fullLabel.textContent = `${this.money(service.price)}, veilig online`;
          if (depositLabel && deposit) depositLabel.textContent = `Restant ${this.money(Math.max(0, service.price - deposit.price))} bij de afspraak`;
        }

        // Samenvatting
        this.querySelector('[data-sum-service]').textContent = service ? service.title : '—';
        this.querySelector('[data-sum-datetime]').textContent = this.datetimeText() || '—';
        this.querySelector('[data-sum-location]').textContent = this.locationText() || '—';

        const isDeposit = this.payment === 'deposit' && deposit;
        const due = service ? (isDeposit ? deposit.price : service.price) : 0;
        this.querySelector('[data-sum-pay-label]').textContent = isDeposit ? 'Nu te betalen' : 'Totaal';
        this.querySelector('[data-sum-total]').textContent = this.money(due);
        const rest = this.querySelector('[data-sum-rest]');
        rest.hidden = !(isDeposit && service);
        if (isDeposit && service) rest.textContent = `Restant ${this.money(Math.max(0, service.price - deposit.price))} betaal je bij de afspraak.`;

        this.querySelector('[data-bar-label]').textContent = service ? this.datetimeText() || service.title : 'Kies je behandeling';
        this.querySelector('[data-bar-total]').textContent = service ? this.money(due) : '';
        this.classList.toggle('has-service', Boolean(service));
      }

      /* ---------- Events ---------- */

      bindEvents() {
        this.form.addEventListener('change', (event) => {
          if (event.target.name === 'service') {
            this.state.service = event.target.value;
            this.clearError();
            this.update();
            this.scrollToStep('datetime');
            return;
          }
          this.update();
        });

        this.daysEl.addEventListener('click', (event) => {
          const button = event.target.closest('[data-day]');
          if (!button || button.disabled) return;
          this.state.date = button.dataset.day;
          this.state.time = null;
          this.update();
        });

        this.timesEl.addEventListener('click', (event) => {
          const button = event.target.closest('[data-time]');
          if (!button) return;
          this.state.time = Number(button.dataset.time);
          this.clearError();
          this.update();
          if (window.matchMedia('(max-width: 989px)').matches) this.scrollToStep('details');
        });

        this.querySelector('[data-days-prev]').addEventListener('click', () => {
          this.state.dayOffset -= this.visibleDayCount();
          this.renderDays();
        });
        this.querySelector('[data-days-next]').addEventListener('click', () => {
          this.state.dayOffset += this.visibleDayCount();
          this.renderDays();
        });

        this.querySelectorAll('[data-submit]').forEach((button) => button.addEventListener('click', () => this.submit()));

        let resizeTimer;
        window.addEventListener('resize', () => {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(() => this.renderDays(), 150);
        });
      }

      preselectService() {
        const params = new URLSearchParams(window.location.search);
        const wanted = params.get('pakket');
        if (!wanted) return;
        const match =
          this.config.services.find((s) => s.id === wanted) || this.config.services.find((s) => s.handle === wanted);
        if (!match) return;
        const input = this.form.querySelector(`input[name="service"][value="${match.id}"]`);
        if (input) input.checked = true;
        this.state.service = match.id;
      }

      /* ---------- Afronden ---------- */

      validate() {
        if (!this.service) return { step: 'service', message: 'Kies eerst een behandeling.' };
        if (!this.state.date || this.state.time === null) return { step: 'datetime', message: 'Kies een dag en een tijd.' };
        if (this.needsAddress) {
          for (const name of ['street', 'zip', 'city']) {
            const input = this.form.elements[name];
            if (!input.value.trim()) {
              return { step: 'details', message: 'Vul het adres volledig in.', focus: input };
            }
          }
        }
        return null;
      }

      async submit() {
        const problem = this.validate();
        if (problem) {
          this.showError(problem.message);
          this.scrollToStep(problem.step);
          if (problem.focus) setTimeout(() => problem.focus.focus({ preventScroll: true }), 350);
          return;
        }

        const service = this.service;
        const deposit = this.config.deposit;
        const isDeposit = this.payment === 'deposit' && deposit;
        const properties = {
          Behandeling: service.title,
          Afspraak: this.datetimeText(),
          Tijdslot: this.slotCode(),
          Locatie: this.locationText(),
        };
        if (this.needsAddress) {
          const f = this.form.elements;
          properties.Adres = `${f.street.value.trim()}, ${f.zip.value.trim().toUpperCase()} ${f.city.value.trim()}`;
        }
        properties.Betaling = isDeposit
          ? `Aanbetaling – restant ${this.money(Math.max(0, service.price - deposit.price))} bij de afspraak`
          : 'Volledig betaald';
        if (isDeposit) properties.Totaalprijs = this.money(service.price);
        if (this.config.status) properties.Status = this.config.status;

        const buttons = this.querySelectorAll('[data-submit]');
        buttons.forEach((b) => {
          b.disabled = true;
          b.classList.add('is-loading');
        });
        this.clearError();

        try {
          await fetch(`${window.Shopify?.routes?.root || '/'}cart/clear.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: isDeposit ? deposit.variantId : service.variantId, quantity: 1, properties }] }),
          });
          if (!response.ok) throw new Error('add failed');
          window.location.href = `${window.Shopify?.routes?.root || '/'}checkout`;
        } catch (error) {
          this.showError('Er ging iets mis. Probeer het opnieuw of mail ons op info@rapidclean.nl.');
          buttons.forEach((b) => {
            b.disabled = false;
            b.classList.remove('is-loading');
          });
        }
      }

      /* ---------- Hulpjes ---------- */

      datetimeText() {
        if (!this.state.date || this.state.time === null || !this.service) return '';
        const date = this.parseKey(this.state.date);
        const day = this.dateFmt.format(date);
        return `${day.charAt(0).toUpperCase()}${day.slice(1)}, ${this.fmtTime(this.state.time)}–${this.fmtTime(this.state.time + this.service.duration)}`;
      }

      /** '04-11-2026 10:00-11:00', in te plakken bij 'Geblokkeerde tijden' */
      slotCode() {
        const [y, m, d] = this.state.date.split('-');
        return `${d}-${m}-${y} ${this.fmtTime(this.state.time)}-${this.fmtTime(this.state.time + this.service.duration)}`;
      }

      locationText() {
        const service = this.service;
        if (!service) return '';
        if (service.location === 'customer') return 'Bij jou op locatie';
        if (this.pickupMode === 'self') {
          const loc = this.form.querySelector('input[name="self_location"]:checked');
          return `Zelf brengen${loc ? ` naar ${loc.value}` : ''}`;
        }
        return 'Ophalen en terugbrengen';
      }

      visibleDayCount() {
        const width = this.daysEl.clientWidth || 600;
        return Math.max(5, Math.min(7, Math.floor((width + 8) / 64)));
      }

      scrollToStep(step) {
        const el = this.querySelector(`[data-step="${step}"]`);
        if (!el) return;
        const top = el.getBoundingClientRect().top + window.scrollY - 96;
        window.scrollTo({ top, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      }

      showError(message) {
        this.querySelectorAll('[data-error]').forEach((el) => {
          el.textContent = message;
          el.hidden = false;
        });
      }

      clearError() {
        this.querySelectorAll('[data-error]').forEach((el) => (el.hidden = true));
      }

      key(date) {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      }

      parseKey(key) {
        const [y, m, d] = key.split('-').map(Number);
        return new Date(y, m - 1, d);
      }

      fmtTime(minutes) {
        return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
      }

      money(cents) {
        return `€ ${(cents / 100).toFixed(2).replace('.', ',')}`;
      }

      escape(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
      }
    }
  );
}
